import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import { toast } from "sonner";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Connexion réussie !");
      navigate("/");
    } catch (e) {
      console.error("Login error:", e);
      toast.error(e.response?.data?.detail || "Erreur de connexion");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_moreati-preview/artifacts/abz6ktff_Copie%20de%20nouveau%20logo%20moreati%20%281%29.png" 
                alt="MOREATI" 
                className="h-14 object-contain"
              />
            </Link>
            <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
              Bienvenue
            </h1>
            <p className="text-stone-600 mt-2">
              Connectez-vous pour accéder à votre espace
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="votre@email.com"
                className="mt-2"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full h-12 text-lg font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? (
                "Connexion..."
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Se connecter
                </>
              )}
            </Button>
          </form>

          <p className="text-center mt-6 text-stone-600">
            Pas encore de compte ?{" "}
            <Link to="/register" className="text-[#C65D47] font-semibold hover:underline" data-testid="register-link">
              Créer un compte
            </Link>
          </p>

          {/* Demo credentials */}
          <div className="mt-8 p-4 bg-stone-50 rounded-xl">
            <p className="text-sm text-stone-500 mb-2">Compte démo admin :</p>
            <p className="text-sm font-mono text-stone-600">admin@moreati.fr / admin123</p>
          </div>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1D3557] to-[#2A4A6D]">
          <img
            src="https://images.unsplash.com/photo-1587032286160-e6b508ed7226?w=1200&h=1600&fit=crop"
            alt="Corse"
            className="w-full h-full object-cover opacity-30 mix-blend-overlay"
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-6 text-[#8C9D78]" style={{ fontFamily: 'Fraunces, serif' }}>
              Il est temps de prendre au sérieux nos rêves
            </h2>
            <p className="text-xl text-white/80">
              Et d'en faire une stratégie
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
